import { Box, IconButton, Menu, MenuItem } from '@mui/material'
import { Dotsmenu } from '@e3dc-react/icons'
import React, { useCallback, useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import useModals from 'src/Hooks/useModals'
import { CompanyInvitationModel } from 'src/Libs/company-settings/models'

interface CompanyUserActionMenuProps {
  item: CompanyInvitationModel
  companyId: number
  onRemove: (token: string) => void
  onResend: (companyId: number, token: string) => void
}

/**
 * Providing all the possible actions for a linked user to a company.
 *
 * Used as the functional additions to the users table in the company profile view.
 *
 * @param item
 * @param onRemove
 * @param onResend
 * @constructor
 */
const CompanyUserActionMenu: React.VFC<CompanyUserActionMenuProps> = ({ item, onRemove, onResend, companyId }) => {
  const { t } = useCustomTranslation()
  const { openConfirmationModal } = useModals()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }
  const closeMenu = (): void => {
    setAnchorEl(null)
  }

  const processRemove = useCallback(
    (close: () => void): void => {
      onRemove(item.token)
      close()
    },
    [onRemove, item]
  )

  const processResend = useCallback(
    (close: () => void): void => {
      onResend(companyId, item.token)
      close()
    },
    [onResend, item, companyId]
  )

  const handleClose = useCallback((): void => {
    closeMenu()
  }, [])

  const handleRemoveDialog = useCallback(
    (event: React.MouseEvent<HTMLElement>): void => {
      openConfirmationModal({
        onConfirm: processRemove,
        buttonText: [t('common.cancel'), t('common.invite-confirm')],
        title: t('user.company.invitation.deletion.confirm.title'),
        text: t('user.company.invitation.deletion.confirm.description'),
      })
      closeMenu()
    },
    [openConfirmationModal, processRemove, t]
  )

  const handleResendDialog = useCallback(
    (event: React.MouseEvent<HTMLElement>): void => {
      openConfirmationModal({
        onConfirm: processResend,
        buttonText: [t('common.cancel'), t('common.invite-confirm')],
        title: t('user.company.reInvitation.dialog.title'),
        text: t('user.company.reInvitation.dialog.text'),
      })
      closeMenu()
    },
    [openConfirmationModal, processResend, t]
  )

  return (
    <Box>
      <IconButton onClick={handleClick}>
        <Dotsmenu />
      </IconButton>
      <Menu id="action-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleRemoveDialog}>{t('user.company.invitation.remove')}</MenuItem>
        <MenuItem onClick={handleResendDialog}>{t('user.company.reInvitation.controlLabel')}</MenuItem>
      </Menu>
    </Box>
  )
}

export default CompanyUserActionMenu
